(() => {
  'use strict';
  const singleton = Symbol.for('ArtMotri.site.interactions.v1');
  if (window[singleton]) return;
  window[singleton] = true;

  // All homepage content is outside this layer, including its complete carousel.
  // No gallery controller, style, geometry, hover or pause state is changed here.
  const homepage = () => /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/i.test(window.location.pathname)
    || Boolean(document.querySelector('#am-moving-gallery, artmotri-home, .am-home'));
  const excluded = 'form, iframe, [data-no-site-interactions], [data-am-interactions="off"], [class^="pify" i], [class*=" pify" i], [id^="pify" i], [data-pify-form], [class*="flits" i], [id*="flits" i]';
  const media = 'img, picture, video, canvas, iframe, [data-ape-view]';
  const marker = 'data-am-site-interaction';
  const ctas = [
    'a.amp-enquire', 'a.amp-return', 'a.amp-artist',
    '.ape-artist-context a', '.ape-related-heading a',
    'a.aps-back', 'a.aps-next', 'a.aps-contact-link', 'button[data-aps-enquire]',
    'a.acs-link', 'a.af-link', 'a.am-enquiry-back',
    'a.ame-link', 'a.ame-tier-link',
    '.am-membership-root a.am-button', '.am-membership-root a.am-text-link',
    '.am-membership-root a.am-subtle-link', '.am-membership-root a.am-service-link'
  ].join(', ');
  const disclosures = 'details.af-item, details.ame-benefits, details.ame-rule, .am-membership-root details';
  const dialogs = 'artmotri-product-experience dialog, artmotri-artist-profile dialog';
  const panels = '.am-membership-root[data-am-my-collection] [data-am-panel]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  const allowed = node => Boolean(node?.isConnected && !homepage() && !node.closest(excluded));
  const safeBody = node => allowed(node) && !node.matches(media) && !node.querySelector(media + ', ' + excluded);
  const collect = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];

  function cancel(record) {
    for (const animation of record.animations) animation.cancel();
    record.animations.clear();
  }

  function play(record, nodes) {
    cancel(record);
    if (reduced.matches || !allowed(record.node)) return;
    for (const node of nodes) {
      if (!safeBody(node) || typeof node.animate !== 'function') continue;
      try {
        const animation = node.animate([
          { opacity: 0, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 240, easing: 'cubic-bezier(.22,.7,.2,1)' });
        record.animations.add(animation);
        // Cancellation is expected on close, preference changes and teardown.
        animation.finished.then(() => record.animations.delete(animation), () => record.animations.delete(animation));
      } catch (_) { /* The native content remains visible without WAAPI. */ }
    }
  }

  function create(node) {
    const record = { node, controller: new AbortController(), animations: new Set(), marks: new Map(), observer: null };
    instances.set(node, record);
    return record;
  }

  function mark(record, node, value) {
    if (!node || !allowed(node)) return;
    record.marks.set(node, node.getAttribute(marker));
    node.setAttribute(marker, value);
  }

  function teardown(node) {
    const record = instances.get(node);
    if (!record) return;
    record.controller.abort();
    record.observer?.disconnect();
    cancel(record);
    for (const [target, previous] of record.marks) {
      if (previous === null) target.removeAttribute(marker);
      else target.setAttribute(marker, previous);
    }
    instances.delete(node);
  }

  function enhanceCTA(node) {
    if (instances.has(node) || !safeBody(node)) return;
    const record = create(node);
    mark(record, node, node.matches('button, a.amp-enquire, a.am-button') ? 'button' : 'link');
  }

  function enhanceDetails(node) {
    if (instances.has(node) || !allowed(node)) return;
    const record = create(node);
    mark(record, node.querySelector('summary'), 'disclosure');
    let previous = node.open;
    // Native toggle owns open/close and accessibility. There is no height tween.
    node.addEventListener('toggle', () => {
      if (previous === node.open) return;
      previous = node.open;
      if (node.open) play(record, [...node.children].filter(child => child.tagName !== 'SUMMARY'));
      else cancel(record);
    }, { signal: record.controller.signal });
  }

  function observeState(node, attribute, read, enter) {
    if (instances.has(node) || !allowed(node) || !('MutationObserver' in window)) return;
    const record = create(node);
    let previous = read();
    record.observer = new MutationObserver(() => {
      const current = read();
      if (current === previous) return;
      previous = current;
      if (current) play(record, enter());
      else cancel(record);
    });
    record.observer.observe(node, { attributes: true, attributeFilter: [attribute] });
  }

  function refresh(scope = document) {
    for (const node of [...instances.keys()]) if (!allowed(node)) teardown(node);
    if (homepage()) return;
    collect(scope, ctas).forEach(enhanceCTA);
    collect(scope, disclosures).forEach(enhanceDetails);
    collect(scope, dialogs).forEach(node => observeState(node, 'open', () => node.open, () =>
      [...node.querySelectorAll('.ape-viewer-header, .ape-dialog-header, .aps-dialog-header')]));
    collect(scope, panels).forEach(node => observeState(node, 'hidden', () => !node.hidden, () => [...node.children]));
  }

  const teardownAll = () => [...instances.keys()].forEach(teardown);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  else refresh();
  reduced.addEventListener('change', () => { if (reduced.matches) instances.forEach(cancel); });
  document.addEventListener('shopify:section:load', event => refresh(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const node of [...instances.keys()]) if (node === event.target || event.target.contains(node)) teardown(node);
  });
  window.addEventListener('pagehide', teardownAll);
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
})();
